import type { Admin } from "@shared/socket-namespaces/admin/admin.namespace";
import type { useQueryClient } from "@tanstack/react-query";

type LeadListRow = Record<string, unknown> & { id: string };
type QueryClient = ReturnType<typeof useQueryClient>;

type LeadsPage = {
  items: LeadListRow[];
  hasMore: boolean;
  limit: number;
  offset: number;
};

type LeadsInfiniteData = {
  pages: LeadsPage[];
  pageParams: unknown[];
};

type LeadsCacheData = LeadListRow[] | LeadsPage | LeadsInfiniteData;

export async function invalidateLeadHandlingQueries(
  queryClient: QueryClient,
  leadId?: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["summer-campaign-leads"] }),
    queryClient.invalidateQueries({ queryKey: ["summer-campaign-leads-stats"] }),
    queryClient.invalidateQueries({
      queryKey: ["summer-campaign-leads-pending-count"],
    }),
    ...(leadId
      ? [
          queryClient.invalidateQueries({
            queryKey: ["summer-campaign-lead-detail", leadId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["summer-campaign-lead-matches", leadId],
          }),
        ]
      : []),
  ]);
}

/** Lightweight counters only - never refetches the full leads list. */
export function refreshLeadHandlingBadges(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: ["summer-campaign-leads-stats"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["summer-campaign-leads-pending-count"],
  });
}

function isInfiniteData(data: LeadsCacheData): data is LeadsInfiniteData {
  return (
    !!data &&
    typeof data === "object" &&
    "pages" in data &&
    Array.isArray((data as LeadsInfiniteData).pages)
  );
}

function isPageData(data: LeadsCacheData): data is LeadsPage {
  return (
    !!data &&
    typeof data === "object" &&
    "items" in data &&
    Array.isArray((data as LeadsPage).items) &&
    !("pages" in data)
  );
}

function findLeadInCaches(
  queryClient: QueryClient,
  leadId: string,
): LeadListRow | undefined {
  const keys = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["summer-campaign-leads"] });

  for (const query of keys) {
    const data = query.state.data as LeadsCacheData | undefined;
    if (!data) continue;

    if (Array.isArray(data)) {
      const found = data.find((lead) => lead.id === leadId);
      if (found) return found;
      continue;
    }

    if (isInfiniteData(data)) {
      for (const page of data.pages) {
        const found = page.items.find((lead) => lead.id === leadId);
        if (found) return found;
      }
      continue;
    }

    if (isPageData(data)) {
      const found = data.items.find((lead) => lead.id === leadId);
      if (found) return found;
    }
  }
  return undefined;
}

function leadExistsInCacheData(data: LeadsCacheData, leadId: string): boolean {
  if (Array.isArray(data)) return data.some((lead) => lead.id === leadId);
  if (isInfiniteData(data)) {
    return data.pages.some((page) =>
      page.items.some((lead) => lead.id === leadId),
    );
  }
  if (isPageData(data)) return data.items.some((lead) => lead.id === leadId);
  return false;
}

/** Map items per page - never flatten (preserves infinite-query pageParams). */
function mapLeadLists(
  queryClient: QueryClient,
  mapper: (tab: unknown, old: LeadListRow[]) => LeadListRow[],
) {
  const keys = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["summer-campaign-leads"] });

  for (const query of keys) {
    const tab = query.queryKey[1];
    queryClient.setQueryData(
      query.queryKey,
      (old: LeadsCacheData | undefined) => {
        if (!old) return old;

        if (Array.isArray(old)) {
          return mapper(tab, old);
        }

        if (isInfiniteData(old)) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: mapper(tab, page.items),
            })),
          };
        }

        if (isPageData(old)) {
          return { ...old, items: mapper(tab, old.items) };
        }

        return old;
      },
    );
  }
}

function prependLeadToMineCaches(
  queryClient: QueryClient,
  lead: LeadListRow,
) {
  const keys = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["summer-campaign-leads", "mine"] });

  for (const query of keys) {
    queryClient.setQueryData(
      query.queryKey,
      (old: LeadsCacheData | undefined) => {
        if (!old) return old;
        if (leadExistsInCacheData(old, lead.id)) return old;

        if (Array.isArray(old)) return [lead, ...old];

        if (isInfiniteData(old)) {
          const first = old.pages[0];
          if (!first) {
            return {
              pages: [
                {
                  items: [lead],
                  hasMore: false,
                  limit: 50,
                  offset: 0,
                },
              ],
              pageParams: [0],
            };
          }
          return {
            ...old,
            pages: [
              { ...first, items: [lead, ...first.items] },
              ...old.pages.slice(1),
            ],
          };
        }

        if (isPageData(old)) {
          return { ...old, items: [lead, ...old.items] };
        }

        return old;
      },
    );
  }
}

export function patchLeadHandlingInList(
  queryClient: QueryClient,
  data: Admin.LeadHandlingChangedEvent,
) {
  const sourceLead = findLeadInCaches(queryClient, data.leadId);
  const handlingPatch = {
    handlingManagerId: data.handlingManagerId,
    handlingManagerName: data.handlingManagerName,
    handlingSince: data.handlingSince,
  };

  mapLeadLists(queryClient, (tab, old) => {
    if (tab === "mine" && !data.handlingManagerId) {
      return old.filter((lead) => lead.id !== data.leadId);
    }

    if (!old.length) return old;
    return old.map((lead) =>
      lead.id === data.leadId ? { ...lead, ...handlingPatch } : lead,
    );
  });

  // Claim: insert into mine tab if the row isn't there yet.
  if (data.handlingManagerId && sourceLead) {
    prependLeadToMineCaches(queryClient, {
      ...sourceLead,
      ...handlingPatch,
    });
  }
}

export function patchLeadFieldsInList(
  queryClient: QueryClient,
  leadId: string,
  fields: Record<string, unknown>,
) {
  mapLeadLists(queryClient, (_tab, old) => {
    if (!old.length) return old;
    return old.map((lead) =>
      lead.id === leadId ? { ...lead, ...fields } : lead,
    );
  });
}

export function patchLeadContactStatusInList(
  queryClient: QueryClient,
  leadId: string,
  contactStatus: string,
) {
  mapLeadLists(queryClient, (_tab, old) => {
    if (!old.length) return old;
    return old.map((lead) =>
      lead.id === leadId ? { ...lead, contactStatus } : lead,
    );
  });

  // Drop from caches whose contactStatus filter no longer matches.
  const keys = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["summer-campaign-leads"] });

  for (const query of keys) {
    const filterContactStatus = readContactStatusFilterFromQueryKey(
      query.queryKey,
    );
    if (!filterContactStatus || filterContactStatus === contactStatus) continue;

    queryClient.setQueryData(
      query.queryKey,
      (old: LeadsCacheData | undefined) => {
        if (!old) return old;
        const drop = (list: LeadListRow[]) =>
          list.filter((lead) => lead.id !== leadId);
        if (Array.isArray(old)) return drop(old);
        if (isInfiniteData(old)) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: drop(page.items),
            })),
          };
        }
        if (isPageData(old)) return { ...old, items: drop(old.items) };
        return old;
      },
    );
  }

  refreshLeadHandlingBadges(queryClient);
}

/** After processLead - clear handling, update status/count, drop from pending/mine. */
export function patchLeadAfterProcess(
  queryClient: QueryClient,
  leadId: string,
  patch: {
    status: string;
    registrationCount?: number;
    contactStatus?: string;
  },
) {
  const sourceLead = findLeadInCaches(queryClient, leadId);
  const processedRow = sourceLead
    ? {
        ...sourceLead,
        status: patch.status,
        ...(patch.registrationCount != null
          ? { registrationCount: patch.registrationCount }
          : {}),
        ...(patch.contactStatus ? { contactStatus: patch.contactStatus } : {}),
        handlingManagerId: null,
        handlingManagerName: null,
        handlingSince: null,
      }
    : null;

  mapLeadLists(queryClient, (tab, old) => {
    if (!old.length) return old;

    if (tab === "pending" || tab === "mine") {
      return old.filter((lead) => lead.id !== leadId);
    }

    return old.map((lead) =>
      lead.id === leadId
        ? {
            ...lead,
            status: patch.status,
            ...(patch.registrationCount != null
              ? { registrationCount: patch.registrationCount }
              : {}),
            ...(patch.contactStatus
              ? { contactStatus: patch.contactStatus }
              : {}),
            handlingManagerId: null,
            handlingManagerName: null,
            handlingSince: null,
          }
        : lead,
    );
  });

  if (processedRow) {
    insertLeadIntoTabIfMissing(queryClient, "processed", processedRow);
    insertLeadIntoTabIfMissing(queryClient, "all", processedRow);
  }

  refreshLeadHandlingBadges(queryClient);
  void queryClient.invalidateQueries({
    queryKey: ["summer-campaign-lead-detail", leadId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["summer-campaign-lead-matches", leadId],
  });
}

/** After decline - remove from active tabs. */
export function patchLeadAfterDecline(queryClient: QueryClient, leadId: string) {
  const sourceLead = findLeadInCaches(queryClient, leadId);
  const declinedRow = sourceLead
    ? {
        ...sourceLead,
        status: "DECLINED",
        handlingManagerId: null,
        handlingManagerName: null,
        handlingSince: null,
      }
    : null;

  mapLeadLists(queryClient, (tab, old) => {
    if (!old.length) return old;
    if (tab === "declined") {
      return old.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              status: "DECLINED",
              handlingManagerId: null,
              handlingManagerName: null,
              handlingSince: null,
            }
          : lead,
      );
    }
    return old.filter((lead) => lead.id !== leadId);
  });

  if (declinedRow) {
    insertLeadIntoTabIfMissing(queryClient, "declined", declinedRow);
  }

  refreshLeadHandlingBadges(queryClient);
}

function insertLeadIntoTabIfMissing(
  queryClient: QueryClient,
  tab: string,
  lead: LeadListRow,
) {
  const keys = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["summer-campaign-leads", tab] });

  for (const query of keys) {
    queryClient.setQueryData(
      query.queryKey,
      (old: LeadsCacheData | undefined) => {
        if (!old) return old;
        if (leadExistsInCacheData(old, lead.id)) {
          // Update in place if present.
          const update = (list: LeadListRow[]) =>
            list.map((row) => (row.id === lead.id ? lead : row));
          if (Array.isArray(old)) return update(old);
          if (isInfiniteData(old)) {
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: update(page.items),
              })),
            };
          }
          if (isPageData(old)) return { ...old, items: update(old.items) };
          return old;
        }

        if (Array.isArray(old)) return [lead, ...old];
        if (isInfiniteData(old)) {
          const first = old.pages[0];
          if (!first) return old;
          return {
            ...old,
            pages: [
              { ...first, items: [lead, ...first.items] },
              ...old.pages.slice(1),
            ],
          };
        }
        if (isPageData(old)) return { ...old, items: [lead, ...old.items] };
        return old;
      },
    );
  }
}

function readContactStatusFilterFromQueryKey(
  queryKey: readonly unknown[],
): string | undefined {
  // Key shapes:
  // ["summer-campaign-leads", tab, sharedFilters]
  const filters = queryKey[2];
  if (!filters || typeof filters !== "object") return undefined;
  const contactStatus = (filters as { contactStatus?: unknown }).contactStatus;
  return typeof contactStatus === "string" && contactStatus
    ? contactStatus
    : undefined;
}
